import { Controller } from "@hotwired/stimulus"

// CONEK-5597 — bridges the HTML5 `<input type="color">` (hex string like
// "#3bbfc6") to the HSL triple format the brand value object expects
// (e.g. "183 55% 50%"). One controller instance per color picker; the
// picker is the visible UI, the hidden HSL field is what the form submits.
//
// Why HSL not hex? The fallback chain in `application.css` is
// `hsl(var(--navbar-bg, var(--card)))` — every brand token cascades through
// HSL. Storing HSL keeps the DB schema aligned with what the browser
// resolves (so a future migration to a richer palette — secondary accents,
// gradients — needs no shape change at the storage layer).
//
// We mirror `SpaceGroup::Brand#hsl_to_hex` (Ruby) so the picker preview
// and the round-tripped server value agree to the bit.
export default class extends Controller {
  static targets = ["picker", "hsl", "preview"]

  // On connect, only paint the preview swatch from whatever the picker
  // currently shows. We deliberately do NOT write the hidden HSL field here:
  // for a default (no-override) tenant the picker shows the design-system
  // fallback hex, but the hidden field is blank (meaning "no override").
  // Writing the fallback's HSL on connect would silently persist a full
  // palette on every save -- corrupting theme_overrides and flipping
  // is_default? to false for every edited SpaceGroup. The hidden field is
  // only written when the user genuinely interacts with the picker
  // (onPickerInput) or explicitly resets .
  connect() {
    this.paintPreview()
  }

  // `<input type="color">` fires `input` on every change.
  onPickerInput() {
    this.syncFromPicker()
  }

  // Reverse sync: if the admin types an HSL triple directly into the text
  // field, update the color picker + preview swatch to match.
  onHslInput() {
    const hsl = this.hasHslTarget ? this.hslTarget.value.trim() : ""
    if (!hsl) return
    const hex = hslToHex(hsl)
    if (hex && this.hasPickerTarget) this.pickerTarget.value = hex
    this.paintPreview()
  }

  // Paint the preview swatch only (no hidden-field write). Safe to call
  // on connect for default tenants -- does not persist anything.
  paintPreview() {
    const hex = this.hasPickerTarget ? this.pickerTarget.value : ""
    if (this.hasPreviewTarget) this.previewTarget.style.backgroundColor = hex
  }

  // Write the hidden HSL field + paint the preview. Called only on genuine
  // user interaction (onPickerInput) so a default tenant's blank
  // hidden field is preserved until the admin actually picks a colour.
  syncFromPicker() {
    const hex = this.hasPickerTarget ? this.pickerTarget.value : ""
    const hsl = hex ? hexToHsl(hex) : ""
    if (this.hasHslTarget) this.hslTarget.value = hsl
    this.paintPreview()
  }
}

// HSL triple -> hex (inverse of hexToHsl). Parses "H S% L%" and returns "#RRGGBB".
function hslToHex(hsl) {
  const parts = hsl.split(/\s+/)
  if (parts.length !== 3) return null
  const h = parseFloat(parts[0])
  const s = parseFloat(parts[1].replace("%", "")) / 100
  const l = parseFloat(parts[2].replace("%", "")) / 100
  if (isNaN(h) || isNaN(s) || isNaN(l)) return null
  const a = s * Math.min(l, 1 - l)
  const f = (n) => {
    const k = (n + h / 30) % 12
    const color = l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1))
    return Math.round(255 * color)
      .toString(16)
      .padStart(2, "0")
  }
  return `#${f(0)}${f(8)}${f(4)}`
}

// HSL conversion matching the Ruby's `SpaceGroup::Brand#hsl_to_hex` inverse.
// Inputs are CSS hex colors as emitted by the HTML5 <input type="color">
// spec — always 7 chars including the leading `#` (e.g. "#3bbfc6"). Output is
// the same `"H S% L%"` format the value object expects.
// (3-char shorthand hex like "#abc" is NOT supported — `padEnd(6, "0")`
// would pad to "abc000" instead of correctly doubling each digit to "aabbcc".
// In practice this is unreachable: <input type="color"> always emits the
// full 6-digit form.)
function hexToHsl(hex) {
  const norm = hex.replace("#", "").padEnd(6, "0")
  const r = parseInt(norm.slice(0, 2), 16) / 255
  const g = parseInt(norm.slice(2, 4), 16) / 255
  const b = parseInt(norm.slice(4, 6), 16) / 255
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const l = (max + min) / 2

  if (max === min) return `0 0% ${Math.round(l * 100)}%`

  const d = max - min
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
  let h = 0
  if (max === r) h = ((g - b) / d) % 6
  else if (max === g) h = (b - r) / d + 2
  else h = (r - g) / d + 4
  h = Math.round(h * 60)
  if (h < 0) h += 360

  return `${h} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`
}
